import { useEffect, useState } from 'react';

import { Filters } from 'coreOld/components';
import { FilterField } from 'coreOld/enums';
import { useSaveFilters } from 'coreOld/hooks';
import { IFilters, IFiltersProps } from 'coreOld/types';
import { getDisplayName, isEmptyObject, removeNullValuesNestedObject } from 'coreOld/utils';

type ValueType = number | string | Record<string, number[] | string[]>;

const deleteObjectKeys = (keys: string[], object) => {
  keys.forEach((e) => delete object[e]);
};

const isInputGroupWithIndividualFields = (name: string, filtersConfig) => {
  const inputGroup = filtersConfig.find((element) => element.name === name);

  return inputGroup && FilterField.INPUT_GROUP === inputGroup.type && inputGroup.individualFields;
};

const getValueToAdd = (name, filtersConfig, value) => {
  if (isInputGroupWithIndividualFields(name, filtersConfig)) {
    return value;
  }

  return { [name]: value };
};

const getDefaultFilters = (
  initialFilters = {},
  getFilters: (key: string) => Record<string, any>,
  filterKey = null,
  rememberFilters = false
) => {
  if (filterKey && rememberFilters) {
    initialFilters = {
      ...initialFilters,
      ...getFilters(filterKey),
    };
  }

  return initialFilters;
};

const getDefaultAdvancedFilters = (defaultFilters, advancedFiltersConfig) => {
  if (!advancedFiltersConfig) {
    return {};
  }

  const advancedFilterKeys = advancedFiltersConfig?.map((config) => config.name);

  const defaultAdvancedFilters = {};
  for (const key in defaultFilters) {
    if (advancedFilterKeys.includes(key)) {
      defaultAdvancedFilters[key] = defaultFilters[key];
    }
  }

  return defaultAdvancedFilters;
};

const withFilters = <T,>(WrappedComponent: React.ComponentType<T>) => {
  const WithFilters = (props: Omit<T & IFilters, keyof IFiltersProps>) => {
    const {
      filtersConfig,
      initialFilters,
      onAdd,
      advancedFiltersConfig,
      filterKey,
      rememberFilters,
    } = props;

    const { getFilters, saveFilters } = useSaveFilters();

    const defaultFilters = getDefaultFilters(
      initialFilters,
      getFilters,
      filterKey,
      rememberFilters
    );

    const defaultAdvancedFilters = getDefaultAdvancedFilters(defaultFilters, advancedFiltersConfig);

    const [filters, setFilters] = useState(defaultFilters);
    const [advancedFilters, setAdvancedFilters] = useState(defaultAdvancedFilters);

    useEffect(() => {
      if (!filterKey || !rememberFilters) {
        return;
      }

      saveFilters(filterKey, filters);
    }, [filters, setFilters]);

    const onChangeFilter = (name: string, value: ValueType) => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        [name]: value,
      }));
    };

    const onChangeAdvancedFilter = (name: string, value: ValueType, clearFilters?: boolean) => {
      const valueToAdd = getValueToAdd(name, advancedFiltersConfig, value);

      const updatedAdvancedFilters = {
        ...advancedFilters,
        ...valueToAdd,
      };

      const isEmptyAdvancedFilters = isEmptyObject(updatedAdvancedFilters, true);

      if (!isEmptyAdvancedFilters) {
        setAdvancedFilters((prevFilters) => ({
          ...prevFilters,
          ...valueToAdd,
        }));
      }

      if (clearFilters || isEmptyAdvancedFilters) {
        deleteObjectKeys(Object.keys(advancedFilters), filters);
        setFilters(filters);
        setAdvancedFilters({});
      }
    };

    const onApplyAdvancedFilters = () => {
      setFilters(removeNullValuesNestedObject({ ...filters, ...advancedFilters }));
    };

    return (
      <>
        <Filters
          onChangeFilter={onChangeFilter}
          onChangeAdvancedFilter={onChangeAdvancedFilter}
          onApplyAdvancedFilters={onApplyAdvancedFilters}
          onAdd={onAdd}
          values={filters}
          advancedFiltersValues={advancedFilters}
          filtersConfig={filtersConfig}
          advancedFiltersConfig={advancedFiltersConfig}
        />

        <WrappedComponent
          {...(props as T & IFilters)}
          filters={filters}
          setFilters={setFilters}
          initialFilters={initialFilters}
        />
      </>
    );
  };

  WithFilters.displayName = `WithFilters(${getDisplayName(WrappedComponent)})`;

  return WithFilters;
};

export default withFilters;
